import React, { useContext, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { MoneyApi } from "go-core/api/types";
import FormatDate, { formatDateTimeToString } from "go-core/components/Formatters/FormatDate";
import { FormDirty } from "go-form/components/FormDirty";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	DeliveryEstimationApi,
	LiveOrderAcceptanceFormApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { OrdersContext } from "../../../../../../services/context";
import {
	MAX_WOLT_DRIVE_DELIVERY_MINUTES,
	isExternalOrderDelivery,
	isWoltDriveAssigned,
} from "../../../../../../services/orderDelivery";
import { liveOrderParamsInclude } from "../../../../../../services/orderSynchronizer";
import { LiveOrderDeliveryOptionType, woltDriveTimeOptions } from "../../../../../../services/types";
import {
	getDefaultDeliveryOption,
	getDefaultOrderEstimatedDeliveryData,
	getLiveOrderNumber,
	getNumberOfMinutesFromNowToTheDate,
} from "../../../../../../services/utils";
import LiveOrderModalEstimatedTimeInfo from "../LiveOrderModalEstimatedTimeInfo";
import LiveOrderAcceptanceModalDeliveryOptions from "./LiveOrderAcceptanceModalDeliveryOptions";
import LiveOrderAcceptanceModalTimeOptions from "./LiveOrderAcceptanceModalTimeOptions";

interface Props {
	show: boolean;
	onHide: () => void;
	order: LiveOrderApi;
}

const LiveOrderAcceptanceModal = ({ onHide, show, order }: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const form = useForm<LiveOrderAcceptanceFormApi>({
		criteriaMode: "all",
	});
	const organization = useSelector(selectOrganization);
	const { handleSubmit, formState, setError, setValue } = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const [selectedTimeOption, setSelectedTimeOption] = useState<number | string | undefined>();
	const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<LiveOrderDeliveryOptionType | undefined>(
		getDefaultDeliveryOption(organization, order)
	);
	const [estimatedDeliveryData, setEstimatedDeliveryData] = useState<DeliveryEstimationApi | undefined>();
	const ordersContext = useContext(OrdersContext);
	const [deliveryEstimatingLoading, setDeliveryEstimatingLoading] = useState(false);
	const [firstEstimatedDeliveryTime, setFirstEstimatedDeliveryTime] = useState<number | undefined>();
	const [firstEstimatedPrice, setFirstEstimatedPrice] = useState<MoneyApi | undefined>();
	const orderEstimatedPreparationInMinutes = useMemo(() => {
		return order.delivery?.delivery_pickup_in_minutes !== undefined ? order.delivery.delivery_pickup_in_minutes : 0;
	}, [order?.estimated_preparation_at]);
	const isWoltDrive = isWoltDriveAssigned(order);
	const assignedDeliveryEmployee =
		order.delivery?.delivery_employee?.reference_id || order.delivery?.delivery_employee?.id || isWoltDrive;
	const currency = organization.currency || "PLN";
	const woltDriveScheduleForm = useForm<{ scheduled_delivery_at?: string }>({
		criteriaMode: "all",
	});
	const [firstEstimatedDeliveryData, setFirstEstimatedDeliveryData] = useState<DeliveryEstimationApi | undefined>(
		isWoltDrive ? getDefaultOrderEstimatedDeliveryData(order) : undefined
	);
	const [estimatingLoading, setEstimatingLoading] = useState(false);

	const getParsedTimeOptionToDataForm = (data: LiveOrderAcceptanceFormApi) => {
		if (typeof selectedTimeOption === "number") {
			const timeToAdd = selectedTimeOption * 60 * 1000;
			const date = new Date(new Date().getTime() + timeToAdd);
			return formatDateTimeToString(date);
		}

		return data.pickup_at;
	};

	const handleCreateWoltDriveDelivery = async () => {
		const deliveryPickupInMinutes =
			typeof selectedTimeOption === "string" ? 0 : estimatedDeliveryData?.delivery_pickup_in_minutes;
		try {
			await apiOrganization.assignEmployeeDeliveryToOrder(order.id, {
				delivery_employee_id: estimatedDeliveryData?.id,
				delivery_pickup_in_minutes: deliveryPickupInMinutes,
				delivery_cost: {
					amount: estimatedDeliveryData?.price?.amount || 0,
					currency,
				},
				scheduled_delivery_at: typeof selectedTimeOption === "string" ? selectedTimeOption : undefined,
			});
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const getEstimatedDeliveryTimeForWoltDrive = async () => {
		try {
			const res = await apiOrganization.estimateOrderDelivery(
				order.id,
				"WOLT_DRIVE",
				0,
				woltDriveScheduleForm.getValues("scheduled_delivery_at")
			);

			return res.delivery_pickup_in_minutes;
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			if (selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE) {
				await handleCreateWoltDriveDelivery();
			}
			if (selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER || isExternalOrderDelivery(order)) {
				data.pickup_at = getParsedTimeOptionToDataForm(data);
			} else if (
				selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE &&
				typeof selectedTimeOption === "string"
			) {
				const deliveryMinutes = await getEstimatedDeliveryTimeForWoltDrive();
				if (deliveryMinutes === undefined) {
					data.estimated_preparation_at = undefined;
				} else {
					const timeToAdd = deliveryMinutes * 60 * 1000;
					data.estimated_preparation_at = formatDateTimeToString(new Date(new Date().getTime() + timeToAdd));
				}
			} else {
				delete data.pickup_at;
				data.estimated_preparation_at = getParsedTimeOptionToDataForm(data);
			}

			const res = await apiOrganization.acceptLiveOrder(order.id, data, { include: liveOrderParamsInclude });
			ordersContext.updateOrder(res);
			addSuccessFlash(t("lib:common.flash.completed"));
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const handleUnselectDeliveryOption = (option: LiveOrderDeliveryOptionType | undefined) => {
		setValue("pickup_at", "");
		woltDriveScheduleForm.setValue("scheduled_delivery_at", undefined);
		if (option === LiveOrderDeliveryOptionType.OWN_DRIVER) {
			woltDriveScheduleForm.setValue("scheduled_delivery_at", undefined);
			if (order.pickup_at) {
				setSelectedTimeOption(order.pickup_at);
				setValue("pickup_at", order.pickup_at);
			} else {
				setSelectedTimeOption(undefined);
			}
		} else if (option === LiveOrderDeliveryOptionType.WOLT_DRIVE) {
			if (!selectedTimeOption && isWoltDrive) {
				if (orderEstimatedPreparationInMinutes) {
					const value =
						orderEstimatedPreparationInMinutes > MAX_WOLT_DRIVE_DELIVERY_MINUTES
							? order.pickup_at
							: orderEstimatedPreparationInMinutes;

					setSelectedTimeOption(value);
					woltDriveScheduleForm.setValue(
						"scheduled_delivery_at",
						typeof value === "string" ? value : undefined
					);
				} else {
					const value =
						estimatedDeliveryData?.delivery_pickup_in_minutes !== undefined &&
						estimatedDeliveryData.delivery_pickup_in_minutes > MAX_WOLT_DRIVE_DELIVERY_MINUTES
							? order.pickup_at
							: estimatedDeliveryData?.delivery_pickup_in_minutes ?? order.pickup_at;

					setSelectedTimeOption(value);
					woltDriveScheduleForm.setValue(
						"scheduled_delivery_at",
						typeof value === "string" ? value : undefined
					);
				}
			} else if (!assignedDeliveryEmployee) {
				const value =
					estimatedDeliveryData?.delivery_pickup_in_minutes &&
					estimatedDeliveryData.delivery_pickup_in_minutes > MAX_WOLT_DRIVE_DELIVERY_MINUTES
						? order.pickup_at
						: estimatedDeliveryData?.delivery_pickup_in_minutes ?? order.pickup_at;

				setSelectedTimeOption(value);
				woltDriveScheduleForm.setValue("scheduled_delivery_at", typeof value === "string" ? value : undefined);
			} else if (orderEstimatedPreparationInMinutes === selectedTimeOption) {
				setSelectedTimeOption(orderEstimatedPreparationInMinutes);
			} else if (
				[...woltDriveTimeOptions, firstEstimatedDeliveryTime].includes(orderEstimatedPreparationInMinutes)
			) {
				setSelectedTimeOption(orderEstimatedPreparationInMinutes);
			} else {
				setSelectedTimeOption(undefined);
			}
		} else {
			setSelectedTimeOption(undefined);
		}
	};

	const isSubmitButtonDisabled = () => {
		if (isExternalOrderDelivery(order)) {
			return !selectedTimeOption;
		}
		return !selectedDeliveryOption || !selectedTimeOption;
	};

	const getDeliveryEstimationForOwnDriver = (): DeliveryEstimationApi => {
		let deliveryPickupInMinutes = 0;

		if (typeof selectedTimeOption === "string") {
			deliveryPickupInMinutes = getNumberOfMinutesFromNowToTheDate(selectedTimeOption);
		} else {
			deliveryPickupInMinutes = selectedTimeOption ?? 0;
		}

		return {
			delivery_pickup_in_minutes: deliveryPickupInMinutes,
			id: "",
			price: { amount: 0, currency: "" },
			pickup_in_minutes: 0,
		};
	};

	return (
		<Modal show={show} onHide={onHide} className="live-order-acceptance-modal" size="lg">
			<FormDirty formState={formState} onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>{`${t("modules.live_order.field.order_acceptance.title")} ${getLiveOrderNumber(
						order
					)}`}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{(order.pickup_at || order.requested_pickup_at) && (
						<>
							<h5 className="mb-2">{t("modules.live_order.field.client_pick_up_date.title")}</h5>
							<div className="live-order-date-box">
								{FormatDate((order.requested_pickup_at ?? order.pickup_at) as string)}
							</div>
							<hr />
						</>
					)}
					{isExternalOrderDelivery(order) ? (
						<h5 className="mb-4">{`${t("modules.live_order.field.external_delivery.title")} ${
							order.delivery?.external_provider_name
						}`}</h5>
					) : (
						<LiveOrderAcceptanceModalDeliveryOptions
							setSelectedDeliveryOption={setSelectedDeliveryOption}
							selectedDeliveryOption={selectedDeliveryOption}
							selectedTimeOption={selectedTimeOption}
							setEstimatedDeliveryData={setEstimatedDeliveryData}
							estimatedDeliveryData={estimatedDeliveryData}
							order={order}
							orderDate={order.pickup_at}
							firstEstimatedDeliveryData={firstEstimatedDeliveryData}
							setFirstEstimatedDeliveryData={setFirstEstimatedDeliveryData}
							handleToggle={handleUnselectDeliveryOption}
							orderEstimatedPreparationInMinutes={orderEstimatedPreparationInMinutes}
							setEstimatingLoading={setEstimatingLoading}
							estimatingLoading={estimatingLoading}
						/>
					)}
					<LiveOrderAcceptanceModalTimeOptions
						setSelectedTimeOption={setSelectedTimeOption}
						selectedTimeOption={selectedTimeOption}
						form={form}
						order={order}
						orderEstimatedPreparationInMinutes={orderEstimatedPreparationInMinutes}
						orderDate={order.pickup_at}
						firstEstimatedDeliveryData={firstEstimatedDeliveryData}
						selectedDeliveryOption={selectedDeliveryOption}
						woltDriveScheduleForm={woltDriveScheduleForm}
						estimatingLoading={estimatingLoading}
					/>
					{selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE && estimatedDeliveryData && (
						<LiveOrderModalEstimatedTimeInfo deliveryEstimation={estimatedDeliveryData} />
					)}
					{selectedDeliveryOption === LiveOrderDeliveryOptionType.OWN_DRIVER && selectedTimeOption && (
						<LiveOrderModalEstimatedTimeInfo
							deliveryEstimation={getDeliveryEstimationForOwnDriver()}
							ownDriver
						/>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading
						loading={loading}
						type="submit"
						onClick={onSubmit}
						disabled={isSubmitButtonDisabled()}
					>
						{t("modules.live_order.action.accept.title")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default LiveOrderAcceptanceModal;
